import React, { useEffect, useRef } from 'react';

/**
 * NetworkBackground
 * --------------------------------------------------------------
 * Lightweight, full-viewport animated network of nodes + edges that
 * sits behind the entire app as subtle texture. Vanilla canvas, no
 * dependencies. Mouse-weighted: nearby nodes drift toward the cursor
 * and edges to the cursor brighten.
 *
 * Performance budget:
 *   - Node count scales with viewport area, capped low on mobile.
 *   - O(n^2) edges, but n is small (~25–55) and we early-cull by dist^2.
 *   - Pauses when tab hidden (visibilitychange).
 *   - Honors prefers-reduced-motion (renders one static frame, then stops).
 *   - DPR capped at 2 to avoid retina meltdown.
 *   - Single rAF loop; mouse events only update a vec, no work per move.
 */
function NetworkBackground() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) return;

        // We honor reduce-motion by suppressing the autonomous drift, but
        // still run the rAF loop so mouse interaction stays responsive.
        // The user explicitly asked for always-on animation as background texture.
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const driftScale = reduceMotion ? 0 : 1;

        // ---- Sizing -------------------------------------------------------
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let width = 0;
        let height = 0;

        const resize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = Math.floor(width * dpr);
            canvas.height = Math.floor(height * dpr);
            canvas.style.width = width + 'px';
            canvas.style.height = height + 'px';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();

        // ---- Node count by viewport + device hint -------------------------
        const isCoarse = window.matchMedia('(pointer: coarse)').matches;
        const lowCore = (navigator.hardwareConcurrency || 4) < 4;
        const baseDensity = isCoarse || lowCore ? 18000 : 28000; // px^2 per node
        const targetCount = Math.round((width * height) / baseDensity);
        const NODE_COUNT = Math.max(14, Math.min(isCoarse ? 28 : 55, targetCount));

        const LINK_DIST = isCoarse ? 130 : 160;
        const LINK_DIST_SQ = LINK_DIST * LINK_DIST;
        const MOUSE_RADIUS = 180;
        const MOUSE_RADIUS_SQ = MOUSE_RADIUS * MOUSE_RADIUS;

        // ---- Node init ----------------------------------------------------
        const rand = (a, b) => a + Math.random() * (b - a);
        const nodes = new Array(NODE_COUNT).fill(0).map(() => ({
            x: Math.random() * width,
            y: Math.random() * height,
            vx: rand(-0.18, 0.18) * driftScale,
            vy: rand(-0.18, 0.18) * driftScale,
            r: rand(1.0, 2.2),
        }));

        // ---- Mouse --------------------------------------------------------
        const mouse = { x: -9999, y: -9999, active: false };
        const onMove = (e) => {
            mouse.x = e.clientX;
            mouse.y = e.clientY;
            mouse.active = true;
        };
        const onLeave = () => {
            mouse.active = false;
            mouse.x = -9999;
            mouse.y = -9999;
        };
        const onTouch = (e) => {
            if (e.touches && e.touches[0]) {
                mouse.x = e.touches[0].clientX;
                mouse.y = e.touches[0].clientY;
                mouse.active = true;
            }
        };
        window.addEventListener('mousemove', onMove, { passive: true });
        window.addEventListener('mouseleave', onLeave);
        window.addEventListener('touchmove', onTouch, { passive: true });
        window.addEventListener('touchend', onLeave);

        // ---- Resize debounce ---------------------------------------------
        let resizeTimer = null;
        const onResize = () => {
            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(resize, 120);
        };
        window.addEventListener('resize', onResize);

        // ---- Visibility pause --------------------------------------------
        let running = true;
        const onVisibility = () => {
            running = !document.hidden;
            if (running) loop();
        };
        document.addEventListener('visibilitychange', onVisibility);

        // ---- Frame loop ---------------------------------------------------
        const draw = () => {
            ctx.clearRect(0, 0, width, height);

            // Update positions
            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                // gentle drift
                n.x += n.vx;
                n.y += n.vy;

                // mouse attraction (soft pull)
                if (mouse.active) {
                    const dx = mouse.x - n.x;
                    const dy = mouse.y - n.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < MOUSE_RADIUS_SQ && d2 > 1) {
                        const f = (1 - d2 / MOUSE_RADIUS_SQ) * 0.04;
                        n.vx += (dx / Math.sqrt(d2)) * f;
                        n.vy += (dy / Math.sqrt(d2)) * f;
                    }
                }

                // velocity damping (keeps system from heating up)
                n.vx *= 0.985;
                n.vy *= 0.985;

                // re-energize if nearly stopped (skip if reduce-motion is on,
                // so the system stays still until the user moves the mouse)
                if (driftScale && Math.abs(n.vx) < 0.04) n.vx += rand(-0.04, 0.04);
                if (driftScale && Math.abs(n.vy) < 0.04) n.vy += rand(-0.04, 0.04);

                // wrap around edges
                if (n.x < -10) n.x = width + 10;
                else if (n.x > width + 10) n.x = -10;
                if (n.y < -10) n.y = height + 10;
                else if (n.y > height + 10) n.y = -10;
            }

            // Draw edges
            for (let i = 0; i < nodes.length; i++) {
                const a = nodes[i];
                for (let j = i + 1; j < nodes.length; j++) {
                    const b = nodes[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 > LINK_DIST_SQ) continue;
                    const t = 1 - d2 / LINK_DIST_SQ; // 0..1
                    // mouse boost: brighten edges whose midpoint is near cursor
                    let boost = 0;
                    if (mouse.active) {
                        const mx = (a.x + b.x) * 0.5;
                        const my = (a.y + b.y) * 0.5;
                        const mdx = mx - mouse.x;
                        const mdy = my - mouse.y;
                        const md2 = mdx * mdx + mdy * mdy;
                        if (md2 < MOUSE_RADIUS_SQ) {
                            boost = (1 - md2 / MOUSE_RADIUS_SQ) * 0.35;
                        }
                    }
                    const alpha = Math.min(0.25, t * 0.18 + boost);
                    // Warm ember tone (logo highlight #ffb066, slightly amber-shifted).
                    ctx.strokeStyle = `rgba(255, 170, 90, ${alpha})`;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();
                }
            }

            // Draw nodes
            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                let glow = 0;
                if (mouse.active) {
                    const dx = mouse.x - n.x;
                    const dy = mouse.y - n.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < MOUSE_RADIUS_SQ) {
                        glow = (1 - d2 / MOUSE_RADIUS_SQ) * 0.5;
                    }
                }
                ctx.fillStyle = `rgba(255, 195, 130, ${0.42 + glow})`;
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r + glow * 1.2, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        let rafId = 0;
        const loop = () => {
            if (!running) return;
            draw();
            rafId = requestAnimationFrame(loop);
        };

        // Always run the loop. Drift is gated by driftScale, so when
        // reduce-motion is set the nodes stay still until the cursor
        // pulls them — user-initiated motion is still allowed.
        loop();

        // ---- Cleanup ------------------------------------------------------
        return () => {
            running = false;
            cancelAnimationFrame(rafId);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseleave', onLeave);
            window.removeEventListener('touchmove', onTouch);
            window.removeEventListener('touchend', onLeave);
            window.removeEventListener('resize', onResize);
            document.removeEventListener('visibilitychange', onVisibility);
            if (resizeTimer) clearTimeout(resizeTimer);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            className="network-bg"
        />
    );
}

export default NetworkBackground;
